import type {
  ITransferPricingRedlineResponse,
  ITransferPricingRedlineSubject,
  ITransferPricingRedlineSummary,
  TTransferPricingRedlineSubjectKind,
} from './types';

interface ITransferPricingRedlineSubjectPath {
  recordPath: string;
  versionSegment: string;
}

/** Paths and body guards for the redline endpoints. The same two routes hang off two different
 * parents, so the only thing that varies between a policy agreement and a documentation record is
 * the path prefix. */
export class TransferPricingRedlineRequestHelper {
  static readonly NO_REDLINES: ITransferPricingRedlineSummary[] = [];

  static readonly FALLBACK_ERROR_MESSAGE =
    'Could not produce the comparison. Please try again.';

  private static readonly SUBJECT_PATHS: Record<
    TTransferPricingRedlineSubjectKind,
    ITransferPricingRedlineSubjectPath
  > = {
    POLICY: {
      recordPath: 'transfer-pricing/policy',
      versionSegment: 'document-version',
    },
    DOCUMENTATION: {
      recordPath: 'transfer-pricing/documentation',
      versionSegment: 'version',
    },
  };

  static versionsPath(subject: ITransferPricingRedlineSubject): string {
    const { recordPath, versionSegment } = this.SUBJECT_PATHS[subject.kind];
    return `${recordPath}/${subject.subjectId}/${versionSegment}`;
  }

  static listPath(subject: ITransferPricingRedlineSubject): string {
    return `${this.versionsPath(subject)}/redlines`;
  }

  static detailPath(
    subject: ITransferPricingRedlineSubject,
    versionId: string
  ): string {
    return `${this.versionsPath(subject)}/${versionId}/redline`;
  }

  static readServerMessage(body: unknown): string | undefined {
    if (typeof body !== 'object' || body === null || !('message' in body)) {
      return undefined;
    }
    const { message } = body;
    if (typeof message === 'string') return message;
    if (Array.isArray(message) && typeof message[0] === 'string') {
      return message[0];
    }
    return undefined;
  }

  /** Confirms the detail envelope before a caller unwraps it. */
  static isRedlineResponse(
    body: unknown
  ): body is ITransferPricingRedlineResponse {
    if (typeof body !== 'object' || body === null || !('redline' in body)) {
      return false;
    }
    const { redline } = body;
    return (
      typeof redline === 'object' &&
      redline !== null &&
      'status' in redline &&
      typeof redline.status === 'string'
    );
  }
}
